import React, { useState, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { LDProvider } from "launchdarkly-react-client-sdk";
import { ThemeProvider } from "@emotion/react";
import store from "../../store";
import theme from "../../themes/theme";
import AuthorizationProvider from "../../common/providers/AuthorizationProvider";
import LaunchDarklyProvider from "../../common/launchDarkly/LaunchDarklyProvider";
import Sidebar, {
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
} from "../ProjectLayout/Sidebar";
import SettingsNavigationProvider, {
  SETTINGS_HEADER_HEIGHT,
} from "./SettingsNavigationProvider";

interface SettingsLayoutProps {
  account_slug: string;
  account_id: string;
}

export const SettingsLayoutInner: React.FC = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  const sidebarWidth = sidebarExpanded
    ? SIDEBAR_EXPANDED_WIDTH
    : SIDEBAR_COLLAPSED_WIDTH;

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--settings-sidebar-width", `${sidebarWidth}px`);
    root.style.setProperty(
      "--settings-nav-height",
      `${SETTINGS_HEADER_HEIGHT}px`
    );
  }, [sidebarWidth]);

  return (
    <>
      <Sidebar
        expanded={sidebarExpanded}
        onToggle={() => setSidebarExpanded((prev) => !prev)}
        reloadDocument
      />
      <SettingsNavigationProvider sidebarWidth={sidebarWidth} />
    </>
  );
};

const SettingsLayout: React.FC<SettingsLayoutProps> = ({ account_slug }) => {
  return (
    <Provider store={store}>
      <AuthorizationProvider account_slug={account_slug}>
        <ThemeProvider theme={theme}>
          <LDProvider
            clientSideID={
              process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID || "dummyclient"
            }
          >
            <LaunchDarklyProvider accountSlug={account_slug}>
              <BrowserRouter>
                <SettingsLayoutInner />
              </BrowserRouter>
            </LaunchDarklyProvider>
          </LDProvider>
        </ThemeProvider>
      </AuthorizationProvider>
    </Provider>
  );
};

export default SettingsLayout;
